import React, {useMemo, useState} from 'react';
import {Image as ImageIcon, Play} from 'lucide-react';
import {AuthenticatedImage} from './AuthenticatedImage.tsx';
import {
    countRoomMedia,
    filterRoomMedia,
    groupRoomMediaByMonth,
    roomMediaEmptyText,
    type RoomMediaFilter,
    type RoomMediaItem,
} from '../../utils/roomMedia.ts';

interface GroupMediaTabProps {
    items: RoomMediaItem[];
    /** Opens the viewer on `index` of the list currently shown. */
    onOpen: (items: RoomMediaItem[], index: number) => void;
    /** Loads older history when the user asks for more. */
    onLoadOlder?: () => void;
    loadingOlder?: boolean;
}

const FILTERS: Array<[RoomMediaFilter, string]> = [['all', 'All'], ['photos', 'Photos'], ['videos', 'Videos']];

/**
 * Multimedia tab of the group panel (D-CHAT-05/S04–S07): All / Photos /
 * Videos chips with counts, then one card per month with a 3-column grid.
 */
export const GroupMediaTab: React.FC<GroupMediaTabProps> = ({items, onOpen, onLoadOlder, loadingOlder}) => {
    const [filter, setFilter] = useState<RoomMediaFilter>('all');
    const counts = useMemo(() => countRoomMedia(items), [items]);
    const shown = useMemo(() => filterRoomMedia(items, filter), [items, filter]);
    const months = useMemo(() => groupRoomMediaByMonth(shown), [shown]);
    const empty = roomMediaEmptyText(filter);

    return (
        <div data-testid="chat.group-panel.media" className="flex flex-col gap-3 bg-canvas pb-3">
            {items.length > 0 && (
                <div role="group" aria-label="Filter media" className="flex gap-1.5 px-3 pt-3">
                    {FILTERS.map(([key, label]) => (
                        <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)}
                                className={`flex h-8 items-center gap-1.5 rounded-lg px-3 text-[13px] font-semibold leading-[1.3] ${
                                    filter === key ? 'bg-brand-tint text-brand-ink' : 'bg-surface text-ink-secondary hover:bg-surface-muted'
                                }`}>
                            <span>{label}</span>
                            <span className="font-medium">{counts[key]}</span>
                        </button>
                    ))}
                </div>
            )}

            {months.length === 0 ? (
                <div className="mx-3 mt-3 flex flex-col items-center gap-2 rounded-2xl bg-surface px-6 py-10 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-muted">
                        <ImageIcon size={22} aria-hidden="true" className="!p-0 !text-ink-muted"/>
                    </span>
                    <div className="text-sm font-semibold text-ink">{empty.title}</div>
                    <div className="text-[13px] leading-[1.4] text-ink-muted">{empty.text}</div>
                </div>
            ) : (
                <div className="flex flex-col gap-3 px-3">
                    {months.map(month => (
                        <section key={month.key} className="flex flex-col gap-3 rounded-2xl bg-surface p-4">
                            <div className="flex items-baseline justify-between">
                                <h2 className="m-0 text-[13px] font-semibold leading-[1.3] text-ink-muted">{month.name}</h2>
                                <span className="text-[13px] font-semibold leading-[1.3] text-ink-muted">{month.items.length}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-1">
                                {month.items.map(item => {
                                    const index = shown.indexOf(item);
                                    const when = new Date(item.ts).toLocaleDateString('en-GB', {day: 'numeric', month: 'short'});
                                    return (
                                        <button key={item.eventId} type="button" onClick={() => onOpen(shown, index)}
                                                data-testid="chat.group-panel.media-tile"
                                                aria-label={`${item.kind === 'video' ? 'Video' : 'Photo'}, ${when}${item.duration ? `, ${item.duration}` : ''}`}
                                                className="relative flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus">
                                            {(item.kind === 'photo' || item.thumbnailUrl) && (
                                                <AuthenticatedImage
                                                    src={item.kind === 'photo' ? item.url : item.thumbnailUrl!}
                                                    alt=""
                                                    loading="lazy"
                                                    className="h-full w-full object-cover"
                                                    encryptedFile={item.kind === 'photo' ? item.encryptedFile : item.thumbnailEncryptedFile}
                                                    mimeType={item.kind === 'photo' ? item.mimeType : undefined}
                                                    thumbnailSrc={item.kind === 'photo' ? item.thumbnailUrl : undefined}
                                                    thumbnailEncryptedFile={item.kind === 'photo' ? item.thumbnailEncryptedFile : undefined}
                                                />
                                            )}
                                            {item.kind === 'video' && (
                                                <>
                                                    <span className="absolute flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(21,20,28,0.8)]">
                                                        <Play size={14} aria-hidden="true" className="!p-0 !text-white" fill="currentColor"/>
                                                    </span>
                                                    {item.duration && (
                                                        <span className="absolute bottom-1.5 right-1.5 rounded-md bg-[rgba(21,20,28,0.8)] px-1.5 py-[3px] text-[13px] font-semibold leading-[1.3] text-white">
                                                            {item.duration}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    ))}
                    {onLoadOlder && (
                        <button type="button" onClick={onLoadOlder} disabled={loadingOlder}
                                className="mx-auto h-8 rounded-lg px-3 text-[13px] font-semibold text-brand-ink hover:bg-brand-tint-subtle disabled:opacity-50">
                            {loadingOlder ? 'Loading…' : 'Load older media'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default GroupMediaTab;
